import React, { useState } from "react";
import Icon from "@components/Icon";

export interface SetupGuideInfoCallout {
    textBefore?: string;
    highlightedText?: string;
    textAfter?: string;
}

export interface SetupGuideAccordionItem {
    title: string;
    content?: React.ReactNode;
    infoCallout?: SetupGuideInfoCallout;
}

interface SetupGuideInfoCalloutBoxProps {
    callout: SetupGuideInfoCallout;
}

const SetupGuideInfoCalloutBox: React.FC<SetupGuideInfoCalloutBoxProps> = ({ callout }) => (
    <div className="flex items-center gap-2.5 rounded border border-[#7dd3fc] bg-blue-05 px-3 py-2">
        <Icon name="info" width={16} height={16} className="shrink-0 fill-[#7DD3FC]" />
        <p className="text-[12px] leading-[18px] tracking-[0.1px] text-text-body">
            {callout.textBefore}
            {callout.highlightedText ? (
                <span className="font-semibold">{callout.highlightedText}</span>
            ) : null}
            {callout.textAfter}
        </p>
    </div>
);

interface SetupGuideAccordionProps {
    step: number;
    title: string;
    children?: React.ReactNode;
    infoCallout?: SetupGuideInfoCallout;
    isExpanded?: boolean;
    defaultExpanded?: boolean;
    onToggle?: () => void;
}

export const SetupGuideAccordion: React.FC<SetupGuideAccordionProps> = ({
    step,
    title,
    children,
    infoCallout,
    isExpanded,
    defaultExpanded = false,
    onToggle,
}) => {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const expanded = isExpanded ?? internalExpanded;
    const hasBody = Boolean(children || infoCallout);

    const handleToggle = () => {
        if (!hasBody) {
            return;
        }

        if (onToggle) {
            onToggle();
            return;
        }

        setInternalExpanded((previous) => !previous);
    };

    return (
        <div className="flex flex-col gap-4 rounded border border-gray-20 bg-white p-2.5">
            <button
                type="button"
                className="flex w-full items-center justify-between gap-2 text-left"
                onClick={handleToggle}
                aria-expanded={expanded}
                disabled={!hasBody}
            >
                <div className="flex min-w-0 items-center gap-2.5">
                    <span className="shrink-0 rounded-full bg-core-plum px-2 py-1 text-[12px] font-medium leading-4 text-white">
                        Step {step}
                    </span>
                    <span className="text-[14px] leading-4 text-black">{title}</span>
                </div>
                {hasBody ? (
                    <Icon
                        name="chevron-down"
                        width={24}
                        height={24}
                        className={`shrink-0 fill-dark-60 transition-transform duration-200 ${
                            expanded ? "rotate-180" : ""
                        }`}
                    />
                ) : (
                    <Icon
                        name="chevron-down"
                        width={24}
                        height={24}
                        className="shrink-0 fill-dark-60"
                    />
                )}
            </button>

            {expanded && hasBody ? (
                <div className="flex flex-col gap-4">
                    {children}
                    {infoCallout ? <SetupGuideInfoCalloutBox callout={infoCallout} /> : null}
                </div>
            ) : null}
        </div>
    );
};

interface SetupGuideAccordionListProps {
    steps: SetupGuideAccordionItem[];
    defaultExpandedIndex?: number | null;
}

const SetupGuideAccordionList: React.FC<SetupGuideAccordionListProps> = ({
    steps,
    defaultExpandedIndex,
}) => {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(
        defaultExpandedIndex === undefined ? 0 : defaultExpandedIndex,
    );

    return (
        <div className="flex flex-col gap-2">
            {steps.map((step, index) => (
                <SetupGuideAccordion
                    key={step.title}
                    step={index + 1}
                    title={step.title}
                    infoCallout={step.infoCallout}
                    isExpanded={expandedIndex === index}
                    onToggle={() => {
                        setExpandedIndex((previous) => (previous === index ? null : index));
                    }}
                >
                    {step.content}
                </SetupGuideAccordion>
            ))}
        </div>
    );
};

export default React.memo(SetupGuideAccordionList);
